import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { Box, Heading, Tab, Tabs } from '@99designs/emu';
import { GridLayout } from '@99designs/marketing-ui';
import { linkTo } from '@99designs/routing';
import { trackEvent } from '@99designs/tracking';
import { DesignCard } from '../components/DesignCard';
import { getDesignInspirationByCategory } from '../util/getDesignInspirationByCategory';
import type { Design, TabId } from '../types';

export interface DesignInspirationTabbedProps {
    categoryKey: string;
    designs: Design[];
    cta: React.ReactNode;
}

export function DesignInspirationTabbed({
    categoryKey,
    designs,
    cta,
}: DesignInspirationTabbedProps) {
    const [activeTab, setActiveTab] = useState<TabId>('latest');
    const [inspirationDesigns, setInspirationDesigns] = useState<Design[]>(designs);
    const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;

    const handleTabClick = async (tabId: string) => {
        setActiveTab(tabId as TabId);
        trackEvent('Inspiration Tab Clicked', { category: categoryKey, tab_name: tabId });

        const inspirationDesignsQueryResult = await getDesignInspirationByCategory(
            apolloClient,
            categoryKey,
            tabId !== 'latest' ? [tabId] : []
        );

        setInspirationDesigns(inspirationDesignsQueryResult.designs);
    };

    const tabs = {
        latest: { name: __('Latest') },
        'real-estate': { name: __('Real estate') },
        travel: { name: __('Travel') },
        food: { name: __('Food') },
        coffee: { name: __('Coffee') },
        medical: { name: __('Medical') },
    };

    return (
        <>
            <Heading appearanceLevel={3} accessibilityLevel={2}>
                {__('Get inspired by these logo design ideas')}
            </Heading>

            <Box marginTop={['1bu', '2bu']} marginBottom={['1bu', '2bu']}>
                <Tabs aria-label={__placeholder('Inspiration Tabs')}>
                    {Object.entries(tabs).map(([id, { name }]) => (
                        <Tab
                            key={id}
                            onClick={(e) => {
                                e.preventDefault();
                                handleTabClick(id as TabId);
                            }}
                            href="#"
                            active={activeTab === id}
                        >
                            {name}
                        </Tab>
                    ))}
                </Tabs>
            </Box>

            <Box display="flex" flexWrap="wrap" justifyContent="space-between">
                {inspirationDesigns.map((design, index) => (
                    <Box key={index} width={['49%', '32%', '24%', '15%']} marginBottom="1bu">
                        <DesignCard
                            src={design.image}
                            displayName={design.designer.displayName}
                            profileUrl={linkTo.userProfile(design.designer.id)}
                            avatarUrl={linkTo.userAvatar(design.designer.id)}
                            marginBottom="0.5bu"
                            width="auto"
                            height="auto"
                            truncateDisplayName={true}
                            onClick={() => {
                                trackEvent('Clicked CTA on Product page', {
                                    category: categoryKey,
                                    ctaLocation: 'design inspiration section',
                                    ctaButton: `designer ${design.designer.displayName} attribution link`,
                                });
                            }}
                        />
                    </Box>
                ))}
            </Box>

            <GridLayout>
                <Box
                    display="flex"
                    justifyContent="center"
                    paddingBottom="0.5bu"
                    marginTop={['1.5bu', '2bu', '2bu', '3bu']}
                >
                    {cta}
                </Box>
            </GridLayout>
        </>
    );
}
